/**
 * Состояние общего аудиоплеера (components/ui/AudioPlayer.tsx): что видит
 * человек на кнопке и под ней.
 *
 * ⚠️ ЗАЧЕМ ОТДЕЛЬНО ОТ КОМПОНЕНТА. Плеер ломается тихо: на телефоне родной
 * `<audio controls>` не показывал загрузку вовсе, и ученик на плохой связи
 * видел «нажал — ничего». Переходы здесь проверяются без браузера.
 *
 * ⚠️ СОБЫТИЯМ ЭЛЕМЕНТА ДО КОНЦА НЕ ВЕРИМ — урок camp (04.09.2026): элемент умеет
 * застрять в загрузке без единого события, и кнопка крутилась бы вечно.
 * Поэтому есть сторож: человек нажал «играть», а признаков жизни нет
 * `STALL_MS` — показываем ошибку с «Повторить».
 *
 * ⚠️ ЗАГРУЗКА ПОКАЗЫВАЕТСЯ, ТОЛЬКО КОГДА ЧЕЛОВЕК ХОЧЕТ СЛУШАТЬ. Элемент читает
 * заголовок файла сразу при открытии страницы (`preload="metadata"`) — крутилка
 * на каждой записи урока до нажатия выглядела бы как вечная загрузка.
 *
 * ⚠️ «ГОТОВ» — ТОЛЬКО ПО `playing`. `canplay` значит «хватит байтов на начало»,
 * а не «звук пошёл»: между ними помещается и отказ браузера, и пустой файл.
 * Правило выглядит как мелочь и ломается одной строкой, поэтому оно закреплено
 * отдельным сценарием проверки.
 */

export type PlayerStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'ended' | 'error';
/** `unsupported` — браузер получил файл, но играть его не умеет (webm на старом Safari). */
export type PlayerError = 'network' | 'unsupported';

export type PlayerState = {
  status: PlayerStatus;
  /** Человек нажал «играть» и ждёт звука. */
  wantPlay: boolean;
  error: PlayerError | null;
  /**
   * Звука нет уже `SOFT_RETRY_MS` — предлагаем повторить загрузку.
   *
   * ⚠️ ЭТО НЕ ОШИБКА. На медленной связи через три секунды файл честно ещё
   * едет, и написать «не загрузилась» значило бы соврать. Плеер продолжает
   * грузить, у человека просто появляется выход раньше, чем через двадцать
   * секунд молчания.
   */
  slow: boolean;
};

export type PlayerEvent =
  | { type: 'src' }
  | { type: 'play-pressed' }
  | { type: 'pause-pressed' }
  | { type: 'retry' }
  | { type: 'loadstart' }
  | { type: 'waiting' }
  | { type: 'canplay' }
  | { type: 'playing' }
  | { type: 'pause' }
  | { type: 'ended' }
  /**
   * `code` — MediaError.code: 2 сеть, 3 не раскодировать, 4 «источник не подошёл».
   * `reachable` — уточнение отдельным запросом, доступен ли файл (см. `errorKind`);
   * приходит вторым событием и меняет только уже показанную ошибку.
   */
  | { type: 'error'; code?: number; reachable?: boolean }
  /** `play()` отклонён; `name` — имя исключения. */
  | { type: 'play-rejected'; name: string }
  | { type: 'slow-timeout' }
  | { type: 'stalled-timeout' };

export const initialPlayer: PlayerState = { status: 'idle', wantPlay: false, error: null, slow: false };

/** Сколько ждать признаков жизни после нажатия, прежде чем признать ошибку. */
export const STALL_MS = 20_000;

/** Через сколько предложить повтор, не объявляя ошибки. */
export const SOFT_RETRY_MS = 3_000;

/**
 * Длиннее этого процент загрузки не показываем.
 *
 * ⚠️ ПОЧЕМУ ПОРОГ ВООБЩЕ ЕСТЬ. Записи IELTS идут до получаса, а files-service
 * отдаёт их кусками (`http.ServeContent`, Range). Браузер тянет окно вокруг
 * места игры, а не файл с начала: `buffered` там говорит о положении, а не о
 * доле скачанного, и «загружено 4 %» у записи, которая играет, — вранье.
 */
export const PERCENT_MAX_SECONDS = 300;

/** Насколько начало буфера может отстоять от нуля и всё ещё считаться началом. */
const PERCENT_START_TOLERANCE = 0.5;

/**
 * Сколько файла скачано — или `null`, когда честно ответить нельзя.
 *
 * ⚠️ `null` ЗНАЧИТ «МОЛЧИМ», А НЕ «НОЛЬ». Показать процент можно только там,
 * где браузер тянет файл подряд с начала: тогда конец буфера и есть доля
 * скачанного. Во всех остальных случаях (длина неизвестна, запись длинная,
 * человек перематывал, буфер разорван) число будет бодрым и неверным —
 * а неверный процент хуже честной крутилки.
 */
export function loadedPercent(i: {
  ranges: [number, number][];
  duration: number | null;
  seeked: boolean;
}): number | null {
  const { ranges, duration, seeked } = i;
  if (duration == null || !Number.isFinite(duration) || duration <= 0) return null;
  if (duration > PERCENT_MAX_SECONDS) return null;
  if (seeked) return null;
  if (ranges.length !== 1) return null;
  const [start, end] = ranges[0];
  if (start > PERCENT_START_TOLERANCE) return null;
  return Math.min(100, Math.floor((end / duration) * 100));
}

/** Скорости по кругу: одна кнопка, каждое нажатие — следующая. */
export const SPEEDS = [1, 1.25, 1.5, 0.75] as const;

export function nextSpeed(rate: number): number {
  const i = SPEEDS.findIndex((s) => s === rate);
  return SPEEDS[(i + 1) % SPEEDS.length];
}

/**
 * Какая это ошибка: «не загрузилось» или «браузер не умеет этот файл».
 *
 * ⚠️ КОД 4 НЕ ЗНАЧИТ «НЕ ПОДДЕРЖИВАЕТСЯ». Браузер отдаёт его и на 404: для него
 * «источник не подошёл» — что чужой формат, что пустой ответ сервера. На стенде
 * 10.09.2026 запись, которой нет на сервере, показала куратору «Этот браузер не
 * может воспроизвести запись» — ложь, после которой человек пошёл бы менять
 * браузер. Поэтому код 4 считается «не поддерживается», только когда файл
 * доступен (`reachable === true`, проверено отдельным запросом).
 */
export function errorKind(code?: number, reachable?: boolean): PlayerError {
  if (code === 3) return 'unsupported';
  if (code === 4 && reachable === true) return 'unsupported';
  return 'network';
}

export function playerReducer(s: PlayerState, e: PlayerEvent): PlayerState {
  switch (e.type) {
    case 'src':
      return initialPlayer;
    case 'play-pressed':
    case 'retry':
      return { status: 'loading', wantPlay: true, error: null, slow: false };
    case 'pause-pressed':
      return { status: 'paused', wantPlay: false, error: null, slow: false };
    case 'loadstart':
    case 'waiting':
      return s.wantPlay && s.status !== 'error' ? { ...s, status: 'loading' } : s;
    case 'canplay':
      // Готов, но звука ещё нет: при желании играть ждём `playing`.
      return s;
    case 'playing':
      // Звук пошёл — предложение повторить снимается вместе с ожиданием.
      return { status: 'playing', wantPlay: true, error: null, slow: false };
    case 'pause':
      return s.status === 'playing' || s.status === 'loading'
        ? { status: 'paused', wantPlay: false, error: null, slow: false }
        : s;
    case 'ended':
      return { status: 'ended', wantPlay: false, error: null, slow: false };
    case 'error':
      // Уточнение пришло, а человек уже нажал «Повторить» — не перебиваем загрузку.
      if (e.reachable !== undefined && s.status !== 'error') return s;
      return { status: 'error', wantPlay: false, error: errorKind(e.code, e.reachable), slow: false };
    case 'play-rejected':
      // AbortError — play() прервали сменой записи или паузой: это не ошибка.
      if (e.name === 'AbortError') return s;
      // NotAllowedError — браузер не дал звук без жеста: ждём нового нажатия.
      if (e.name === 'NotAllowedError') return { status: 'paused', wantPlay: false, error: null, slow: false };
      // ⚠️ NotSupportedError сам по себе НЕ доказывает «браузер не умеет». Chrome
      // отдаёт его и когда источника нет вовсе («no supported sources» — тот же
      // 404/503). На стенде 10.09.2026 именно он переписал верное «не загрузилась»
      // на «браузер не может». Вид ошибки уточняет проверка файла (errorKind);
      // уже показанный вид не трогаем.
      return {
        status: 'error',
        wantPlay: false,
        error: s.status === 'error' && s.error ? s.error : 'network',
        slow: false,
      };
    case 'slow-timeout':
      // Ждём звука и не дождались — предлагаем повтор, оставаясь в загрузке.
      return s.status === 'loading' && s.wantPlay ? { ...s, slow: true } : s;
    case 'stalled-timeout':
      return s.status === 'loading' && s.wantPlay
        ? { status: 'error', wantPlay: false, error: 'network', slow: false }
        : s;
  }
}
